import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  productApi, 
  netValueApi, 
  redemptionApi, 
  dividendApi, 
  performanceFeeApi,
  profitLossApi 
} from '../../services/privateFundApi';
import './ProductDetail.css';

/**
 * 产品详情页面
 */
const ProductDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [netValueHistory, setNetValueHistory] = useState([]);
  const [redemption, setRedemption] = useState(null);
  const [dividends, setDividends] = useState([]);
  const [performanceFees, setPerformanceFees] = useState([]);
  const [profitLoss, setProfitLoss] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, netValue, redemption, dividend, analysis
  const [showAddInvestorDialog, setShowAddInvestorDialog] = useState(false);
  const [investorList, setInvestorList] = useState([]);
  const [newInvestor, setNewInvestor] = useState({
    name: '',
    amount: ''
  });
  const [savingInvestors, setSavingInvestors] = useState(false);
  const [showEditProfitLossDialog, setShowEditProfitLossDialog] = useState(false);
  const [editProfitLossData, setEditProfitLossData] = useState({
    profitLoss: ''
  });
  const [savingProfitLoss, setSavingProfitLoss] = useState(false);

  useEffect(() => {
    fetchProductDetail();
  }, [id]);

  const fetchProductDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 并行获取所有数据
      const [
        productRes,
        historyRes,
        redemptionRes,
        dividendsRes,
        feesRes,
        profitLossRes
      ] = await Promise.all([
        productApi.getProductById(id),
        netValueApi.getNetValueHistory(id, { page: 1, pageSize: 50 }),
        redemptionApi.getRedemption(id).catch(() => ({ success: false, data: null })),
        dividendApi.getDividends(id).catch(() => ({ success: false, data: [] })),
        performanceFeeApi.getPerformanceFees(id).catch(() => ({ success: false, data: [] })),
        profitLossApi.getProductProfitLoss(id).catch(() => ({ success: false, data: null }))
      ]);

      if (productRes.success) {
        setProduct(productRes.data);
        // 初始化投资人员列表
        if (productRes.data.productInvestors && productRes.data.productInvestors.length > 0) {
          setInvestorList(productRes.data.productInvestors.map(pi => ({
            id: pi._id,
            name: pi.investorId?.name || '未知',
            amount: pi.investmentAmount || 0
          })));
        } else {
          setInvestorList([]);
        }
      }
      if (historyRes.success) {
        setNetValueHistory(historyRes.data || []);
      }
      if (redemptionRes.success && redemptionRes.data) {
        setRedemption(redemptionRes.data);
      }
      if (dividendsRes.success) {
        setDividends(dividendsRes.data || []);
      }
      if (feesRes.success) {
        setPerformanceFees(feesRes.data || []);
      }
      if (profitLossRes.success) {
        setProfitLoss(profitLossRes.data);
      }
    } catch (err) {
      console.error('获取产品详情失败:', err);
      setError(err.message || '获取产品详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/private-fund');
  };

  const handleEdit = () => {
    navigate(`/private-fund/products/${id}/edit`);
  };

  const handleUpdateNetValue = () => {
    navigate(`/private-fund/products/${id}/net-value`);
  };

  const handleAddNetValueHistory = () => {
    navigate(`/private-fund/products/${id}/net-value-history`);
  };

  const handleCreateRedemption = () => {
    navigate(`/private-fund/products/${id}/redemption`);
  };

  const handleCreateDividend = () => {
    navigate(`/private-fund/products/${id}/dividend`);
  };

  const handleDeleteProduct = async () => {
    if (!window.confirm('确定要删除这个产品吗？此操作不可恢复！')) {
      return;
    }

    try {
      const response = await productApi.deleteProduct(id);
      if (response.success) {
        navigate('/private-fund');
      } else {
        alert(response.message || '删除产品失败');
      }
    } catch (err) {
      console.error('删除产品失败:', err);
      alert(err.message || '删除产品失败');
    }
  };

  const handleAddInvestor = () => {
    setShowAddInvestorDialog(true);
    setNewInvestor({ name: '', amount: '' });
    setError(null);
  };

  const handleAddInvestorSubmit = async (e) => {
    e.preventDefault();
    
    if (!newInvestor.name.trim()) {
      setError('请输入投资人姓名');
      return;
    }
    
    const amount = parseFloat(newInvestor.amount);
    if (!amount || amount <= 0) {
      setError('请输入有效的投资金额');
      return;
    }

    // 计算当前总金额
    const currentTotal = investorList.reduce((sum, inv) => sum + inv.amount, 0);
    const newTotal = currentTotal + amount;

    // 验证总金额不超过申购金额
    if (newTotal > product.purchaseAmount) {
      setError(`投资总金额不能超过申购金额（${product.purchaseAmount.toLocaleString()} 元）`);
      return;
    }
    
    // 添加到列表
    setInvestorList(prev => [...prev, {
      id: `temp_${Date.now()}`,
      name: newInvestor.name.trim(),
      amount: amount
    }]);
    
    // 关闭对话框并清空表单
    setShowAddInvestorDialog(false);
    setNewInvestor({ name: '', amount: '' });
    setError(null);
  };

  const handleRemoveInvestor = (index) => {
    setInvestorList(prev => prev.filter((_, i) => i !== index));
  };

  const handleCancelAddInvestor = () => {
    setShowAddInvestorDialog(false);
    setNewInvestor({ name: '', amount: '' });
    setError(null);
  };

  const handleSaveInvestors = async () => {
    if (investorList.length === 0) {
      alert('请至少添加一个投资人');
      return;
    }

    // 计算总金额
    const totalAmount = investorList.reduce((sum, inv) => sum + inv.amount, 0);
    
    // 验证总金额不超过申购金额
    if (totalAmount > product.purchaseAmount) {
      alert(`投资总金额（${totalAmount.toLocaleString()} 元）不能超过申购金额（${product.purchaseAmount.toLocaleString()} 元）`);
      return;
    }

    try {
      setSavingInvestors(true);
      setError(null);

      // 格式化投资人员文本（格式：姓名 金额（万元））
      const investorsText = investorList.map(inv => {
        const amountInWan = inv.amount / 10000; // 转换为万元
        return `${inv.name} ${amountInWan}`;
      }).join('\n');

      // 更新产品
      const response = await productApi.updateProduct(id, {
        investors: investorsText
      });

      if (response.success) {
        // 重新获取产品详情
        await fetchProductDetail();
        alert('投资人员保存成功');
      } else {
        setError(response.message || '保存失败');
      }
    } catch (err) {
      console.error('保存投资人员失败:', err);
      setError(err.message || '保存失败');
    } finally {
      setSavingInvestors(false);
    }
  };

  // 处理编辑盈亏
  const handleEditProfitLoss = () => {
    // 如果有赎回记录，使用现有盈亏；否则使用0
    const currentProfitLoss = redemption ? (redemption.profitLoss || 0) : 0;
    setEditProfitLossData({
      profitLoss: currentProfitLoss
    });
    setShowEditProfitLossDialog(true);
  };

  // 保存编辑的盈亏
  const handleSaveProfitLoss = async (e) => {
    e.preventDefault();
    
    const profitLoss = parseFloat(editProfitLossData.profitLoss);
    if (isNaN(profitLoss)) {
      setError('请输入有效的盈亏金额');
      return;
    }

    try {
      setSavingProfitLoss(true);
      setError(null);

      // 使用新的接口直接设置最终盈亏（如果没有赎回记录则创建，有则更新）
      const response = await redemptionApi.setFinalProfitLoss(id, profitLoss);

      if (response.success) {
        await fetchProductDetail();
        setShowEditProfitLossDialog(false);
        alert('盈亏设置成功，个人盈亏已重新计算');
      } else {
        setError(response.message || '设置失败');
      }
    } catch (err) {
      console.error('保存盈亏失败:', err);
      setError(err.message || '保存失败');
    } finally {
      setSavingProfitLoss(false);
    }
  };

  // 取消编辑盈亏
  const handleCancelEditProfitLoss = () => {
    setShowEditProfitLossDialog(false);
    setEditProfitLossData({ profitLoss: '' });
    setError(null);
  };

  if (loading) {
    return (
      <div className="product-detail">
        <div className="loading-message">
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail">
        <div className="error-message">
          <p>错误: {error || '产品不存在'}</p>
          <button onClick={handleBack}>返回</button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail">
      <div className="detail-header">
        <button className="back-button" onClick={handleBack}>
          ← 返回
        </button>
        <h1 className="detail-title">{product.name}</h1>
        <div className="header-actions">
          <button className="edit-button" onClick={handleEdit}>
            编辑
          </button>
          <button className="delete-button" onClick={handleDeleteProduct}>
            删除
          </button>
        </div>
      </div>

      <div className="detail-content">
        {/* 基本信息卡片 */}
        <div className="info-card">
          <h2>基本信息</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">申购时间:</span>
              <span className="info-value">{new Date(product.purchaseTime).toLocaleDateString()}</span>
            </div>
            <div className="info-item">
              <span className="info-label">申购金额:</span>
              <span className="info-value">{product.purchaseAmount.toLocaleString()} 元</span>
            </div>
            <div className="info-item">
              <span className="info-label">申购净值:</span>
              <span className="info-value">{product.purchaseNetValue}</span>
            </div>
            <div className="info-item">
              <span className="info-label">申购份额:</span>
              <span className="info-value">{product.purchaseShares?.toLocaleString()}</span>
            </div>
            {product.latestNetValue && (
              <div className="info-item">
                <span className="info-label">最新净值:</span>
                <span className="info-value">{product.latestNetValue}</span>
              </div>
            )}
            {product.latestNetValueDate && (
              <div className="info-item">
                <span className="info-label">最新净值日期:</span>
                <span className="info-value">{new Date(product.latestNetValueDate).toLocaleDateString()}</span>
              </div>
            )}
            <div className="info-item">
              <span className="info-label">状态:</span>
              <span className={`status-badge ${product.status === 'holding' ? 'holding' : 'redeemed'}`}>
                {product.status === 'holding' ? '持有' : '已赎回'}
              </span>
            </div>
          </div>
        </div>

        {/* 盈亏分析卡片 */}
        {profitLoss && (
          <div className="info-card">
            <div className="section-header-with-button">
              <h2>盈亏分析</h2>
              {product.status === 'redeemed' && (
                <button 
                  className="edit-button"
                  onClick={handleEditProfitLoss}
                >
                  编辑最终盈亏
                </button>
              )}
            </div>
            <div className="profit-loss-info">
              <div className="profit-loss-item">
                <span className="label">盈亏金额:</span>
                <span className={`value ${profitLoss.profitLoss >= 0 ? 'profit' : 'loss'}`}>
                  {profitLoss.profitLoss >= 0 ? '+' : ''}{profitLoss.profitLoss?.toLocaleString() || 0} 元
                </span>
              </div>
              <div className="profit-loss-item">
                <span className="label">盈亏比例:</span>
                <span className={`value ${profitLoss.profitLossRatio >= 0 ? 'profit' : 'loss'}`}>
                  {profitLoss.profitLossRatio >= 0 ? '+' : ''}{(profitLoss.profitLossRatio * 100)?.toFixed(2) || 0}%
                </span>
              </div>
              <div className="profit-loss-item">
                <span className="label">计算方式:</span>
                <span className="value">{profitLoss.calculationMethod}</span>
              </div>
            </div>
          </div>
        )}

        {/* 标签页 */}
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            概览
          </button>
          <button 
            className={`tab ${activeTab === 'netValue' ? 'active' : ''}`}
            onClick={() => setActiveTab('netValue')}
          >
            净值历史
          </button>
          <button 
            className={`tab ${activeTab === 'redemption' ? 'active' : ''}`}
            onClick={() => setActiveTab('redemption')}
          >
            赎回
          </button>
          <button 
            className={`tab ${activeTab === 'dividend' ? 'active' : ''}`}
            onClick={() => setActiveTab('dividend')}
          >
            分红
          </button>
          <button 
            className={`tab ${activeTab === 'analysis' ? 'active' : ''}`}
            onClick={() => setActiveTab('analysis')}
          >
            分析
          </button>
        </div>

        {/* 标签页内容 */}
        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="info-card">
                <div className="section-header-with-button">
                  <h3>投资人员</h3>
                  <button 
                    type="button" 
                    className="add-investor-button"
                    onClick={handleAddInvestor}
                  >
                    + 添加人员
                  </button>
                </div>
                {investorList.length > 0 ? (
                  <div className="investor-list">
                    {investorList.map((investor, index) => (
                      <div key={investor.id || index} className="investor-item">
                        <div className="investor-info">
                          <span className="investor-name">{investor.name}</span>
                          <span className="investor-amount">
                            {investor.amount.toLocaleString('zh-CN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })} 元
                          </span>
                        </div>
                        <button
                          type="button"
                          className="remove-investor-button"
                          onClick={() => handleRemoveInvestor(index)}
                          title="删除"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <div className="investor-total">
                      <span>总金额:</span>
                      <span className={investorList.reduce((sum, inv) => sum + inv.amount, 0) > product.purchaseAmount ? 'error' : ''}>
                        {investorList.reduce((sum, inv) => sum + inv.amount, 0).toLocaleString('zh-CN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })} 元
                      </span>
                      <span className="purchase-amount-hint">
                        / {product.purchaseAmount.toLocaleString()} 元
                      </span>
                    </div>
                    <div className="investor-actions">
                      <button
                        type="button"
                        className="save-investors-button"
                        onClick={handleSaveInvestors}
                        disabled={savingInvestors}
                      >
                        {savingInvestors ? '保存中...' : '保存'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="empty-investor-list">
                    <p>暂无投资人员，点击"添加人员"按钮添加</p>
                  </div>
                )}
              </div>
              {product.historyNote && (
                <div className="info-card">
                  <h3>历史说明</h3>
                  <pre className="history-note">{product.historyNote}</pre>
                </div>
              )}
              {product.status === 'holding' && (
                <div className="quick-actions">
                  <button className="action-button primary" onClick={handleUpdateNetValue}>
                    更新最新净值
                  </button>
                  <button className="action-button" onClick={handleAddNetValueHistory}>
                    添加历史净值
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'netValue' && (
            <div className="net-value-tab">
              <div className="section-header">
                <h3>历史净值记录</h3>
                {product.status === 'holding' && (
                  <button className="action-button" onClick={handleAddNetValueHistory}>
                    + 添加历史净值
                  </button>
                )}
              </div>
              {netValueHistory.length === 0 ? (
                <div className="empty-message">暂无历史净值记录</div>
              ) : (
                <div className="history-list">
                  {netValueHistory.map((record) => (
                    <div key={record._id} className="history-item">
                      <div className="history-date">{new Date(record.netValueDate).toLocaleDateString()}</div>
                      <div className="history-value">净值: {record.netValue}</div>
                      {record.weeklyReturn !== null && (
                        <div className={`history-return ${record.weeklyReturn >= 0 ? 'profit' : 'loss'}`}>
                          周收益: {record.weeklyReturn >= 0 ? '+' : ''}{(record.weeklyReturn * 100).toFixed(2)}%
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'redemption' && (
            <div className="redemption-tab">
              {redemption ? (
                <div className="info-card">
                  <div className="section-header-with-button">
                    <h3>赎回记录</h3>
                    {product.status === 'redeemed' && (
                      <button 
                        className="edit-button"
                        onClick={() => {
                          setEditProfitLossData({
                            profitLoss: redemption.profitLoss || 0
                          });
                          setShowEditProfitLossDialog(true);
                        }}
                      >
                        编辑盈亏
                      </button>
                    )}
                  </div>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">赎回时间:</span>
                      <span className="info-value">{new Date(redemption.redemptionTime).toLocaleDateString()}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">赎回金额:</span>
                      <span className="info-value">{redemption.redemptionAmount.toLocaleString()} 元</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">盈亏:</span>
                      <span className={`info-value ${redemption.profitLoss >= 0 ? 'profit' : 'loss'}`}>
                        {redemption.profitLoss >= 0 ? '+' : ''}{redemption.profitLoss?.toLocaleString() || 0} 元
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">盈亏比例:</span>
                      <span className={`info-value ${redemption.profitLossRatio >= 0 ? 'profit' : 'loss'}`}>
                        {redemption.profitLossRatio >= 0 ? '+' : ''}{(redemption.profitLossRatio * 100)?.toFixed(2) || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-message">
                  <p>暂无赎回记录</p>
                  {product.status === 'holding' && (
                    <button className="action-button" onClick={handleCreateRedemption}>
                      记录赎回
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'dividend' && (
            <div className="dividend-tab">
              <div className="section-header">
                <h3>分红记录</h3>
                {product.status === 'holding' && (
                  <button className="action-button" onClick={handleCreateDividend}>
                    + 记录分红
                  </button>
                )}
              </div>
              {dividends.length === 0 ? (
                <div className="empty-message">暂无分红记录</div>
              ) : (
                <div className="dividend-list">
                  {dividends.map((dividend) => (
                    <div key={dividend._id} className="dividend-item">
                      <div className="dividend-date">{new Date(dividend.dividendTime).toLocaleDateString()}</div>
                      <div className="dividend-shares">分红后份额: {dividend.sharesAfterDividend.toLocaleString()}</div>
                      {dividend.note && (
                        <div className="dividend-note">说明: {dividend.note}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'analysis' && profitLoss && (
            <div className="analysis-tab">
              {profitLoss.investorDetails && profitLoss.investorDetails.length > 0 && (
                <div className="info-card">
                  <h3>投资人员盈亏分配</h3>
                  <div className="investor-list">
                    {profitLoss.investorDetails.map((investor, index) => (
                      <div key={index} className="investor-item">
                        <div className="investor-name">投资人员ID: {investor.investorId}</div>
                        <div className="investor-info">
                          <span>投资金额: {investor.investmentAmount.toLocaleString()} 元</span>
                          <span className={`profit-loss ${investor.profitLoss >= 0 ? 'profit' : 'loss'}`}>
                            盈亏: {investor.profitLoss >= 0 ? '+' : ''}{investor.profitLoss?.toLocaleString() || 0} 元
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 添加投资人对话框 */}
      {showAddInvestorDialog && (
        <div className="add-investor-dialog-overlay" onClick={handleCancelAddInvestor}>
          <div className="add-investor-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="add-investor-dialog-header">
              <h3>添加投资人员</h3>
              <button className="close-button" onClick={handleCancelAddInvestor}>×</button>
            </div>
            <form onSubmit={handleAddInvestorSubmit} className="add-investor-form">
              {error && (
                <div className="error-message">
                  <p>{error}</p>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="investorName">姓名 *</label>
                <input
                  type="text"
                  id="investorName"
                  value={newInvestor.name}
                  onChange={(e) => setNewInvestor({ ...newInvestor, name: e.target.value })}
                  placeholder="请输入投资人姓名"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="investorAmount">投资金额（元）*</label>
                <input
                  type="number"
                  id="investorAmount"
                  value={newInvestor.amount}
                  onChange={(e) => setNewInvestor({ ...newInvestor, amount: e.target.value })}
                  placeholder="请输入投资金额"
                  min="0.01"
                  step="0.01"
                  required
                />
                <div className="form-hint">
                  当前总金额: {investorList.reduce((sum, inv) => sum + inv.amount, 0).toLocaleString('zh-CN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} 元 / 申购金额: {product.purchaseAmount.toLocaleString()} 元
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={handleCancelAddInvestor}>
                  取消
                </button>
                <button type="submit" className="submit-button">
                  添加
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 编辑盈亏对话框 */}
      {showEditProfitLossDialog && (
        <div className="add-investor-dialog-overlay" onClick={handleCancelEditProfitLoss}>
          <div className="add-investor-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="add-investor-dialog-header">
              <h3>编辑最终盈亏</h3>
              <button className="close-button" onClick={handleCancelEditProfitLoss}>×</button>
            </div>
            <form onSubmit={handleSaveProfitLoss} className="add-investor-form">
              {error && (
                <div className="error-message">
                  <p>{error}</p>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="profitLoss">最终盈亏金额（元）*</label>
                <input
                  type="number"
                  id="profitLoss"
                  value={editProfitLossData.profitLoss}
                  onChange={(e) => setEditProfitLossData({ profitLoss: e.target.value })}
                  placeholder="请输入盈亏金额（正数为盈利，负数为亏损）"
                  step="0.01"
                  required
                />
                {redemption && (
                  <div className="form-hint">
                    当前盈亏: {redemption.profitLoss >= 0 ? '+' : ''}{redemption.profitLoss?.toLocaleString() || 0} 元
                    ({redemption.profitLossRatio >= 0 ? '+' : ''}{((redemption.profitLossRatio || 0) * 100).toFixed(2)}%)
                  </div>
                )}
                {!redemption && (
                  <div className="form-hint">
                    当前无赎回记录，将自动创建赎回记录
                  </div>
                )}
                <div className="form-hint">
                  申购金额: {product.purchaseAmount.toLocaleString()} 元
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={handleCancelEditProfitLoss}>
                  取消
                </button>
                <button type="submit" className="submit-button" disabled={savingProfitLoss}>
                  {savingProfitLoss ? '保存中...' : '保存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;

